import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Settings, 
  Save,
  Volume2,
  Maximize2,
  Minimize2,
  ArrowLeft,
  Sparkles,
  Brain,
  Zap,
  ChevronRight,
  RotateCcw,
  ExternalLink,
  Monitor,
  AlertCircle
} from 'lucide-react'
import { useStoryStore } from '../stores/storyStore'

export const StoryPlayer = () => {
  const { storyId } = useParams()
  const navigate = useNavigate()
  const { stories, currentStory, setCurrentStory } = useStoryStore()
  
  // 播放状态
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0)
  const [currentDialogueIndex, setCurrentDialogueIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [volume, setVolume] = useState(0.7)
  const [isAutoPlay, setIsAutoPlay] = useState(false)
  const [showChoices, setShowChoices] = useState(false)
  const [gameHistory, setGameHistory] = useState([])
  const [isTyping, setIsTyping] = useState(false)
  const [displayedText, setDisplayedText] = useState('')
  
  // 全屏和窗口状态
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showWindowOptions, setShowWindowOptions] = useState(false)
  const [isPlayerMode, setIsPlayerMode] = useState(false)

  // 获取当前镜头数据
  const getCurrentShot = useCallback(() => {
    if (!currentStory?.nodes || currentStory.nodes.length === 0) return null
    return currentStory.nodes[currentSceneIndex] || null
  }, [currentStory, currentSceneIndex])

  // 获取当前对话数据
  const getCurrentDialogue = useCallback(() => {
    const scene = getCurrentShot()
    if (!scene?.data?.dialogues || scene.data.dialogues.length === 0) return null
    return scene.data.dialogues[currentDialogueIndex] || null
  }, [getCurrentShot, currentDialogueIndex])

  // 获取角色信息
  const getCharacterInfo = useCallback((characterId) => {
    if (!currentStory?.characters) return null
    return currentStory.characters.find(char => char.id === characterId) || null
  }, [currentStory])

  // 打字机效果
  const typeText = useCallback((text, onComplete) => {
    setIsTyping(true)
    setDisplayedText('')
    let index = 0
    
    const typeInterval = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1))
        index++
      } else {
        clearInterval(typeInterval)
        setIsTyping(false)
        if (onComplete) onComplete()
      }
    }, 30) // 30ms per character
    
    return () => clearInterval(typeInterval)
  }, [])

  // 检测播放器模式
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const mode = urlParams.get('mode')
    setIsPlayerMode(mode === 'player')
  }, [])

  // 加载故事数据
  useEffect(() => {
    const loadStoryData = async () => {
      console.log('🔍 播放器：正在加载故事...', storyId)
      console.log('📚 当前stories数组:', stories.length, '个故事')
      
      // 先从内存中的stories数组查找
      let story = stories.find(s => s.id === storyId)
      
      // 如果内存中没有，尝试从IndexedDB加载
      if (!story) {
        console.log('⚠️ 内存中未找到故事，尝试从IndexedDB加载...')
        try {
          const { enhancedStorageService } = await import('../services/enhancedStorageService')
          story = await enhancedStorageService.default.getStory(storyId)
          if (story) {
            console.log('✅ 从IndexedDB加载故事成功')
          }
        } catch (error) {
          console.error('❌ 从IndexedDB加载故事失败:', error)
        }
      }
      
      if (story) {
        console.log('✅ 故事加载成功:', story.title)
        console.log('📊 故事数据:', {
          nodes: story.nodes?.length || 0,
          edges: story.edges?.length || 0,
          characters: story.characters?.length || 0
        })
        
        // 验证故事数据完整性
        if (!story.nodes || story.nodes.length === 0) {
          console.warn('⚠️ 故事没有nodes数据，可能无法正常播放')
          toast.error('故事数据不完整，无法播放', {
            duration: 4000
          })
          return
        }
        
        setCurrentStory(story)
        // 重置播放状态
        setCurrentSceneIndex(0)
        setCurrentDialogueIndex(0)
        setGameHistory([])
        
        console.log('🎮 播放器初始化完成，准备播放')
      } else {
        console.error('❌ 未找到故事:', storyId)
        toast.error('未找到该故事', {
          duration: 3000
        })
        // 3秒后返回我的游戏页面
        setTimeout(() => {
          navigate('/my-games')
        }, 3000)
      }
    }
    
    loadStoryData()
  }, [storyId, stories, setCurrentStory, navigate])

  // 打字机效果 - 当对话改变时触发
  useEffect(() => {
    const currentDialogue = getCurrentDialogue()
    if (currentDialogue?.content) {
      typeText(currentDialogue.content)
    }
  }, [getCurrentDialogue, typeText])

  // 自动播放逻辑
  useEffect(() => {
    if (isAutoPlay && !isTyping && !showChoices) {
      const timer = setTimeout(() => {
        handleNextDialogue()
      }, 2000) // 2秒后自动播放下一句
      return () => clearTimeout(timer)
    }
  }, [isAutoPlay, isTyping, showChoices])

  // 处理下一句对话
  const handleNextDialogue = useCallback(() => {
    const scene = getCurrentShot()
    if (!scene?.data?.dialogues) return

    if (currentDialogueIndex < scene.data.dialogues.length - 1) {
      setCurrentDialogueIndex(prev => prev + 1)
    } else {
      // 当前镜头对话结束，检查是否有分支选项
      const choices = scene.data.choices || scene.data.branches || []
      console.log('检查分支选项:', choices)
      if (choices.length > 0) {
        setShowChoices(true)
      } else {
        // 没有分支选项，进入下一个镜头
        handleNextScene()
      }
    }
  }, [getCurrentShot, currentDialogueIndex])

  // 处理下一个镜头
  const handleNextScene = useCallback(() => {
    if (!currentStory?.nodes || !currentStory?.edges) return

    const currentScene = getCurrentShot()
    if (!currentScene) return

    // 查找当前镜头的连接关系
    const outgoingEdges = currentStory.edges.filter(edge => edge.source === currentScene.id)
    
    if (outgoingEdges.length > 0) {
      // 如果有连接，跳转到第一个目标镜头
      const targetSceneId = outgoingEdges[0].target
      const targetIndex = currentStory.nodes.findIndex(node => node.id === targetSceneId)
      if (targetIndex !== -1) {
        setCurrentSceneIndex(targetIndex)
        setCurrentDialogueIndex(0)
        setShowChoices(false)
        return
      }
    }

    // 如果没有连接关系，按顺序跳转到下一个镜头
    if (currentSceneIndex < currentStory.nodes.length - 1) {
      setCurrentSceneIndex(prev => prev + 1)
      setCurrentDialogueIndex(0)
      setShowChoices(false)
    } else {
      // 故事结束
      setIsPlaying(false)
      setIsAutoPlay(false)
    }
  }, [currentStory, currentSceneIndex, getCurrentShot])

  // 处理分支选择
  const handleChoice = useCallback((choice) => {
    console.log('处理分支选择:', choice)
    console.log('当前故事数据:', currentStory)
    console.log('当前镜头:', getCurrentShot())
    
    // 记录选择历史
    setGameHistory(prev => [...prev, {
      sceneIndex: currentSceneIndex,
      dialogueIndex: currentDialogueIndex,
      choice: choice
    }])

    const scene = getCurrentShot()
    if (!scene || !currentStory?.edges) {
      console.log('镜头或连接数据不存在')
      return
    }

    // 查找当前镜头的所有出边
    const outgoingEdges = currentStory.edges.filter(edge => edge.source === scene.id)
    console.log('当前镜头的出边:', outgoingEdges)

    // 根据分支选择查找对应的连接
    let targetEdge = null
    
    // 方法1: 通过分支ID查找对应的连接（优先使用）
    if (choice.id) {
      targetEdge = outgoingEdges.find(edge => 
        edge.sourceHandle && edge.sourceHandle.includes(choice.id)
      )
      console.log('通过分支ID查找连接:', choice.id, targetEdge)
    }
    
    // 方法2: 通过分支文本查找对应的连接
    if (!targetEdge && choice.text) {
      targetEdge = outgoingEdges.find(edge => 
        edge.label === choice.text
      )
      console.log('通过分支文本查找连接:', choice.text, targetEdge)
    }
    
    // 方法3: 通过分支的targetNodeId查找对应的连接
    if (!targetEdge && choice.targetNodeId) {
      targetEdge = outgoingEdges.find(edge => 
        edge.target === choice.targetNodeId
      )
      console.log('通过targetNodeId查找连接:', choice.targetNodeId, targetEdge)
    }
    
    // 方法4: 如果有多个出边，按顺序选择
    if (!targetEdge && outgoingEdges.length > 0) {
      const choiceIndex = (scene.data.choices || scene.data.branches || []).findIndex(c => c.id === choice.id)
      if (choiceIndex >= 0 && choiceIndex < outgoingEdges.length) {
        targetEdge = outgoingEdges[choiceIndex]
        console.log('通过顺序查找连接:', choiceIndex, targetEdge)
      }
    }

    console.log('找到的目标连接:', targetEdge)

    if (targetEdge) {
      // 根据连接跳转到目标镜头
      const targetIndex = currentStory.nodes.findIndex(node => node.id === targetEdge.target)
      if (targetIndex !== -1) {
        console.log('跳转到镜头索引:', targetIndex, '目标镜头:', currentStory.nodes[targetIndex])
        setCurrentSceneIndex(targetIndex)
        setCurrentDialogueIndex(0)
        setShowChoices(false)
        return
      }
    }

    // 如果通过连接关系没找到，尝试直接使用targetNodeId
    if (choice.targetNodeId) {
      const targetIndex = currentStory.nodes.findIndex(node => node.id === choice.targetNodeId)
      if (targetIndex !== -1) {
        console.log('通过targetNodeId跳转到镜头索引:', targetIndex)
        setCurrentSceneIndex(targetIndex)
        setCurrentDialogueIndex(0)
        setShowChoices(false)
        return
      }
    }

    // 如果还是没找到，尝试通过章节和镜头号查找
    const targetChapter = choice.chapter || 1
    const targetScene = choice.scene || 1
    const targetNode = currentStory.nodes.find(node => 
      node.data?.chapter === targetChapter && node.data?.scene === targetScene
    )
    if (targetNode) {
      const targetIndex = currentStory.nodes.findIndex(node => node.id === targetNode.id)
      console.log('通过章节镜头号跳转到镜头索引:', targetIndex)
      setCurrentSceneIndex(targetIndex)
      setCurrentDialogueIndex(0)
      setShowChoices(false)
    } else {
      console.log('未找到目标镜头，分支选择失败')
      console.log('尝试查找的章节:', targetChapter, '镜头:', targetScene)
      console.log('可用节点:', currentStory.nodes.map(n => ({ id: n.id, chapter: n.data?.chapter, scene: n.data?.scene })))
    }
  }, [currentSceneIndex, currentDialogueIndex, getCurrentShot, currentStory])

  // 处理播放/暂停
  const handlePlayPause = () => {
    setIsPlaying(!isPlaying)
    setIsAutoPlay(!isAutoPlay)
  }

  // 处理上一个镜头
  const handlePrevious = () => {
    if (currentSceneIndex > 0) {
      setCurrentSceneIndex(prev => prev - 1)
      setCurrentDialogueIndex(0)
      setShowChoices(false)
    }
  }

  // 处理下一个镜头
  const handleNext = () => {
    handleNextScene()
  }

  // 重新开始
  const handleRestart = () => {
    setCurrentSceneIndex(0)
    setCurrentDialogueIndex(0)
    setShowChoices(false)
    setGameHistory([])
    setIsPlaying(false)
    setIsAutoPlay(false)
  }

  // 点击屏幕继续
  const handleScreenClick = () => {
    if (isTyping) return
    if (showChoices) return
    
    handleNextDialogue()
  }

  // 处理全屏切换
  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true)
      }).catch(err => {
        console.error('进入全屏失败:', err)
      })
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false)
      }).catch(err => {
        console.error('退出全屏失败:', err)
      })
    }
  }

  // 处理独立窗口
  const handleOpenInNewWindow = () => {
    // 创建一个新的URL，添加播放器模式参数
    const playerUrl = new URL(window.location.href)
    playerUrl.searchParams.set('mode', 'player')
    playerUrl.searchParams.set('storyId', storyId)
    
    const newWindow = window.open(
      playerUrl.toString(),
      'storyPlayer',
      'width=1280,height=720,scrollbars=no,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
    )
    
    if (newWindow) {
      newWindow.focus()
      // 传递当前播放状态到新窗口
      newWindow.addEventListener('load', () => {
        newWindow.postMessage({
          type: 'STORY_PLAYER_STATE',
          data: {
            storyId,
            currentSceneIndex,
            currentDialogueIndex,
            isPlaying,
            isAutoPlay,
            volume
          }
        }, '*')
      })
    }
  }

  // 监听全屏状态变化
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showWindowOptions && !event.target.closest('.relative')) {
        setShowWindowOptions(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [showWindowOptions])

  // 星空粒子效果
  useEffect(() => {
    const createParticles = () => {
      const particlesContainer = document.getElementById('particles-player')
      if (!particlesContainer) return

      const particleCount = 25
      
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div')
        
        const size = Math.random() * 2 + 0.5
        const posX = Math.random() * 100
        const posY = Math.random() * 100
        const delay = Math.random() * 5
        const duration = Math.random() * 20 + 10
        
        particle.style.cssText = `
          position: absolute;
          width: ${size}px;
          height: ${size}px;
          background: rgba(6, 182, 212, 0.3);
          border-radius: 50%;
          top: ${posY}%;
          left: ${posX}%;
          box-shadow: 0 0 ${size * 2}px rgba(6, 182, 212, 0.5);
          animation: float ${duration}s ease-in-out ${delay}s infinite;
        `
        
        particlesContainer.appendChild(particle)
      }
    }

    createParticles()
  }, [])

  if (!currentStory) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center space-y-6">
          <div className="w-24 h-24 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-cyan-500/25">
            <Brain size={48} className="text-white" />
          </div>
          <div className="text-2xl font-bold text-white">故事加载中...</div>
          <div className="w-8 h-8 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin mx-auto"></div>
          <button
            onClick={() => navigate('/my-games')}
            className="mt-4 px-6 py-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            返回我的游戏
          </button>
        </div>
      </div>
    )
  }
  
  // 检查故事数据完整性
  if (!currentStory.nodes || currentStory.nodes.length === 0) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-24 h-24 bg-gradient-to-br from-red-400 to-red-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-red-500/25">
            <AlertCircle size={48} className="text-white" />
          </div>
          <div className="text-2xl font-bold text-white">故事数据不完整</div>
          <p className="text-gray-400">
            该故事缺少必要的场景数据，无法正常播放。
            请返回编辑器完善故事内容。
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate('/my-games')}
              className="px-6 py-3 text-white bg-gray-600 hover:bg-gray-700 rounded-xl transition-all duration-300"
            >
              返回我的游戏
            </button>
            <button
              onClick={() => navigate(`/editor/${currentStory.id}`)}
              className="px-6 py-3 text-white bg-cyan-500 hover:bg-cyan-600 rounded-xl transition-all duration-300"
            >
              编辑故事
            </button>
          </div>
        </div>
      </div>
    )
  }

  const currentScene = getCurrentShot()
  const currentDialogue = getCurrentDialogue()
  const currentCharacter = currentDialogue ? getCharacterInfo(currentDialogue.characterId) : null

  return (
    <div className="min-h-screen bg-dark relative overflow-hidden avg-player">
      {/* 星空粒子背景效果 */}
      <div id="particles-player" className="fixed inset-0 z-0 opacity-20 pointer-events-none"></div>
      
      {/* 16:9 容器 */}
      <div className="relative w-full" style={{ aspectRatio: '16/9', maxHeight: '100vh' }}>
        {/* 背景图片 - 支持多种字段名 */}
        {(currentScene?.data?.backgroundImage || currentScene?.data?.sceneImage || currentScene?.data?.image) ? (
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${currentScene.data.backgroundImage || currentScene.data.sceneImage || currentScene.data.image})` }}
          >
            <div className="absolute inset-0 avg-background-overlay"></div>
          </div>
        ) : (
          <div className="absolute inset-0 bg-dark"></div>
        )}

        {/* 顶部控制栏 - 仅在非播放器模式下显示 */}
        {!isPlayerMode && (
        <div className="absolute top-0 left-0 right-0 z-20 backdrop-blur-xl bg-black/30 border-b border-white/10">
          <div className="flex items-center justify-between p-3 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/my-games')}
              className="flex items-center gap-2 px-3 py-1.5 text-white hover:text-cyan-400 hover:bg-white/10 rounded-xl transition-all duration-300"
            >
              <ArrowLeft size={16} />
              <span className="text-sm">返回</span>
            </button>
            <h1 className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              {currentStory.title}
            </h1>
            <div className="text-xs text-white/70">
              第 {currentSceneIndex + 1} 章 / 共 {currentStory.nodes?.length || 0} 章
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleRestart}
              className="p-1.5 text-white hover:text-cyan-400 hover:bg-white/10 rounded-xl transition-all duration-300"
              title="重新开始"
            >
              <RotateCcw size={16} />
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-1.5 text-white hover:text-cyan-400 hover:bg-white/10 rounded-xl transition-all duration-300"
              title="设置"
            >
              <Settings size={16} />
            </button>
            
            {/* 窗口选项下拉菜单 */}
            <div className="relative">
              <button
                onClick={() => setShowWindowOptions(!showWindowOptions)}
                className="p-1.5 text-white hover:text-cyan-400 hover:bg-white/10 rounded-xl transition-all duration-300"
                title="窗口选项"
              >
                <Monitor size={16} />
              </button>
              
              {showWindowOptions && (
                <div className="absolute right-0 top-full mt-2 bg-black/80 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl py-2 min-w-[200px] z-50">
                  <button
                    onClick={() => {
                      handleFullscreen()
                      setShowWindowOptions(false)
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-white hover:bg-white/10 flex items-center gap-2 rounded-lg transition-colors duration-200"
                  >
                    {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                    {isFullscreen ? '退出全屏' : '全屏播放'}
                  </button>
                  <button
                    onClick={() => {
                      handleOpenInNewWindow()
                      setShowWindowOptions(false)
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-white hover:bg-white/10 flex items-center gap-2 rounded-lg transition-colors duration-200"
                  >
                    <ExternalLink size={16} />
                    独立窗口
                  </button>
                </div>
              )}
            </div>
                      </div>
          </div>
        </div>
        )}

        {/* 主要内容区域 - 点击继续 */}
        <div 
          className="absolute inset-0 z-10 cursor-pointer"
          onClick={handleScreenClick}
        >
          {/* 播放器模式下的简单控制 */}
          {isPlayerMode && (
            <div className="absolute top-4 right-4 z-30 flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handlePlayPause()
                }}
                className="p-2 bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 rounded-lg transition-all duration-300"
                title={isPlaying ? '暂停' : '播放'}
              >
                {isPlaying ? <Pause size={16} /> : <Play size={16} />}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleRestart()
                }}
                className="p-2 bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 rounded-lg transition-all duration-300"
                title="重新开始"
              >
                <RotateCcw size={16} />
              </button>
            </div>
          )}
          <div className="h-full flex flex-col">
            {/* 镜头标题 */}
            {currentScene?.data?.title && (
              <div className="text-center py-4">
                <h2 className="text-2xl font-bold text-white mb-2">
                  {currentScene.data.title}
                </h2>
                {currentScene.data.description && (
                  <p className="text-white/70 text-base">
                    {currentScene.data.description}
                  </p>
                )}
              </div>
            )}

            {/* 对话区域 - 左下角三分之一 */}
            <div className="flex-1 flex items-end justify-center px-6 pb-20">
              <div className="w-full max-w-4xl">
              {/* 角色对话框 */}
              {currentDialogue && (
                <div className="avg-dialogue-box rounded-2xl p-4 mb-6 avg-fade-in">
                  <div className="flex items-start gap-3">
                    {/* 角色头像 */}
                    <div className="flex-shrink-0">
                      {currentCharacter?.image ? (
                        <img 
                          src={currentCharacter.image} 
                          alt={currentCharacter.name}
                          className="w-12 h-12 rounded-full object-cover avg-character-avatar"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center avg-character-avatar">
                          <span className="text-white font-bold text-sm">
                            {currentCharacter?.name?.charAt(0) || '?'}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* 对话内容 */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-white">
                          {currentCharacter?.name || '未知角色'}
                        </h3>
                        {isTyping && (
                          <div className="flex gap-1">
                            <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce"></div>
                            <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                            <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                          </div>
                        )}
                      </div>
                      <p className="text-white/90 text-base leading-relaxed">
                        {displayedText || currentDialogue.content}
                        {isTyping && <span className="avg-typing-cursor">|</span>}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* 分支选项 */}
              {showChoices && (currentScene?.data?.choices || currentScene?.data?.branches) && (
                <div className="space-y-3 avg-slide-up">
                  <div className="text-center mb-3">
                    <p className="text-white/70 text-xs">
                      请选择你的行动...
                    </p>
                  </div>
                  {(currentScene.data.choices || currentScene.data.branches || []).map((choice, index) => (
                    <button
                      key={choice.id || index}
                      onClick={(e) => {
                        e.stopPropagation()
                        console.log('点击分支选项:', choice)
                        handleChoice(choice)
                      }}
                      className="w-full avg-choice-button rounded-xl p-3 text-left group hover:bg-white/10 transition-all duration-300"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="text-white font-medium text-base mb-1">
                            {choice.text || choice.label || `选项 ${index + 1}`}
                          </h4>
                          {choice.consequence && (
                            <p className="text-white/70 text-xs">
                              {choice.consequence}
                            </p>
                          )}
                          {/* 调试信息 */}
                          {process.env.NODE_ENV === 'development' && (
                            <p className="text-white/50 text-xs mt-1">
                              ID: {choice.id} | 目标: {choice.targetNodeId || choice.targetSceneId || '未设置'}
                            </p>
                          )}
                        </div>
                        <ChevronRight size={16} className="text-white/50 group-hover:text-white transition-colors" />
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* 点击继续提示 */}
              {!isTyping && !showChoices && (
                <div className="text-center">
                  <div className="inline-flex items-center gap-1 text-white/60 text-xs">
                    <span>点击继续</span>
                    <ChevronRight size={12} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 底部播放控制栏 - 仅在非播放器模式下显示 */}
        {!isPlayerMode && (
        <div className="absolute bottom-0 left-0 right-0 z-20 avg-control-bar">
          <div className="flex items-center justify-between p-3 max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <button
              onClick={handlePrevious}
              className="p-2 text-white hover:text-cyan-400 hover:bg-white/10 rounded-xl transition-all duration-300"
              disabled={currentSceneIndex === 0}
            >
              <SkipBack size={20} />
            </button>
            
            <button
              onClick={handlePlayPause}
              className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center text-white hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:shadow-cyan-500/40 transform hover:scale-105"
            >
              {isPlaying ? <Pause size={24} /> : <Play size={24} />}
            </button>
            
            <button
              onClick={handleNext}
              className="p-2 text-white hover:text-cyan-400 hover:bg-white/10 rounded-xl transition-all duration-300"
              disabled={currentSceneIndex >= (currentStory.nodes?.length || 0) - 1}
            >
              <SkipForward size={20} />
            </button>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Volume2 size={16} className="text-white" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                className="w-20 h-1.5 bg-white/20 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #06b6d4 0%, #06b6d4 ${volume * 100}%, rgba(255,255,255,0.2) ${volume * 100}%, rgba(255,255,255,0.2) 100%)`
                }}
              />
            </div>
            
            <div className="text-white text-sm font-medium">
              镜头 {currentSceneIndex + 1} / {currentStory.nodes?.length || 0}
            </div>
          </div>
        </div>
      </div>
        )}
      </div>

      {/* 设置面板 */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-black/80 backdrop-blur-xl rounded-3xl p-8 w-full max-w-md border border-white/10">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">播放设置</h3>
            <div className="space-y-6">
              <div>
                <label className="block text-white text-sm font-medium mb-2">音量</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={(e) => setVolume(e.target.value)}
                  className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #06b6d4 0%, #06b6d4 ${volume * 100}%, rgba(255,255,255,0.2) ${volume * 100}%, rgba(255,255,255,0.2) 100%)`
                  }}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-white font-medium">自动播放</h4>
                  <p className="text-white/70 text-sm">自动播放下一句对话</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={isAutoPlay}
                    onChange={(e) => setIsAutoPlay(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
                </label>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSettings(false)}
                  className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all duration-300"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  )
}